import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/pricing/plans');
        setPlans(res.plans || []);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load plans');
      }
    })();
  }, []);

  function startCheckout(planId) {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/checkout?plan=${planId}`)}`);
      return;
    }
    nav(`/checkout?plan=${planId}`);
  }

  const assignments = plans.filter((p) => p.category === 'assignment');
  const dissertations = plans.filter((p) => p.category === 'dissertation');
  const bulk = plans.filter((p) => p.category === 'bulk');

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4" style={{ position: 'relative' }}>
          <div className="text-center text-white">
            <span className="badge bg-warning text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
              <i className="fas fa-coins me-1"></i>Pay as you go
            </span>
            <h1 style={{ fontWeight: 800, fontSize: '2.8rem' }}>Buy credits</h1>
            <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 640, margin: '0 auto' }}>
              One assignment = 1 credit. A full dissertation = 31 credits. No subscriptions, no
              monthly fees — just buy what you need.
            </p>
          </div>
        </div>
      </section>

      <div className="main-container">
        {err && <div className="alert alert-danger">{err}</div>}

        {assignments.length > 0 && (
          <PlanSection title="Assignment plans" plans={assignments} onBuy={startCheckout} authed={!!user} />
        )}
        {dissertations.length > 0 && (
          <PlanSection title="Dissertation plans" plans={dissertations} onBuy={startCheckout} authed={!!user} />
        )}
        {bulk.length > 0 && (
          <PlanSection title="Bulk & custom" plans={bulk} onBuy={startCheckout} authed={!!user} />
        )}

        <div className="card mt-5">
          <div className="card-body">
            <h5 className="mb-2">
              <i className="fas fa-shield-alt me-2" style={{ color: 'var(--primary)' }}></i>
              Anti-gaming guarantee
            </h5>
            <p className="text-muted mb-0">
              Credits are consumed only when generation completes successfully. Failed jobs get
              automatically refunded. Abandon a generation and we won't charge you.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function PlanSection({ title, plans, onBuy, authed }) {
  return (
    <section className="mt-5">
      <h3 className="mb-3" style={{ fontWeight: 700 }}>
        {title}
      </h3>
      <div className="row g-3">
        {plans.map((p) => (
          <div key={p.id} className="col-md-4">
            <PlanCard plan={p} onBuy={() => onBuy(p.id)} authed={authed} />
          </div>
        ))}
      </div>
    </section>
  );
}

function PlanCard({ plan, onBuy, authed }) {
  return (
    <div
      className="card h-100"
      style={{
        position: 'relative',
        border: plan.popular ? '2px solid var(--accent)' : undefined,
        overflow: 'visible',
      }}
    >
      {plan.popular && (
        <div
          style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, var(--accent), #d97706)',
            color: '#fff',
            padding: '4px 14px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
          }}
        >
          <i className="fas fa-star me-1"></i>Most Popular
        </div>
      )}
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          color: '#fff',
          padding: '1.4rem',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <h4 className="mb-1" style={{ fontWeight: 700 }}>
          {plan.name}
        </h4>
        <div style={{ opacity: 0.85, fontSize: '0.9rem' }}>
          {plan.credits} credit{plan.credits > 1 ? 's' : ''}
        </div>
      </div>
      <div className="card-body d-flex flex-column">
        <div className="mb-3">
          <span style={{ fontSize: 36, fontWeight: 800, color: '#1e293b' }}>K{plan.price_zmw}</span>
          <span className="text-muted ms-1">ZMW</span>
          <div className="text-muted small mt-1">K{plan.per_credit} per credit</div>
        </div>
        <p className="text-muted small flex-grow-1">{plan.description}</p>
        <button type="button" onClick={onBuy} className="btn btn-cta w-100 mt-2">
          {authed ? (
            <>
              <i className="fas fa-shopping-cart me-1"></i>Buy now
            </>
          ) : (
            <>
              <i className="fas fa-user-plus me-1"></i>Sign up &amp; buy
            </>
          )}
        </button>
      </div>
    </div>
  );
}
